import { Box, Typography } from "@mui/material";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { palette } from "@/theme";
import { StyledSnackbar } from "./Toast.styled";

interface ToastProps {
  open: boolean;
  onClose: () => void;
  message: string;
  status?: "success" | "error" | "warning" | "info";
}

export const Toast = ({
  open,
  onClose,
  message,
  status = "success",
}: ToastProps) => {
  const handleClose = () => {
    onClose();
  };

  const toastNode = (
    <StyledSnackbar
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      open={open}
      onClose={handleClose}
      message={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Box
            component="img"
            sx={{ flexShrink: 0 }}
            src={
              status === "success"
                ? "/icons/check-circle.svg"
                : "/icons/exclamation-circle.svg"
            }
          />
          <Typography
            variant="body1"
            sx={{
              color: palette.neutral[0],
              minWidth: 0,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {message || "Sucesso"}
          </Typography>
        </Box>
      }
      key={message}
      status={status}
      autoHideDuration={4000}
    />
  );

  if (typeof document === "undefined") {
    return toastNode;
  }

  return createPortal(toastNode, document.body);
};

/* Global toast API */
type ToastStatus = NonNullable<ToastProps["status"]>;
type ShowHandler = (message: string, status?: ToastStatus) => void;

let showHandlerRef: ShowHandler | null = null;

const callToast = (message: string, status: ToastStatus) => {
  if (showHandlerRef) {
    showHandlerRef(message, status);
  } else {
    // If handler not ready, defer to next tick
    setTimeout(() => {
      if (showHandlerRef) {
        showHandlerRef(message, status);
      }
    }, 0);
  }
};

export const toast = {
  show: (message: string, status: ToastStatus = "success") => {
    callToast(message, status);
  },
  success: (message: string) => {
    callToast(message, "success");
  },
  error: (message: string) => {
    callToast(message, "error");
  },
  warning: (message: string) => {
    callToast(message, "warning");
  },
  info: (message: string) => {
    callToast(message, "info");
  },
};

/* Provider to render the Toast and register the global handler */
export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<ToastStatus>("success");

  const show = useCallback<ShowHandler>((msg, st = "success") => {
    setMessage(msg);
    setStatus(st);
    setOpen(true);
  }, []);

  useEffect(() => {
    showHandlerRef = show;
    return () => {
      showHandlerRef = null;
    };
  }, [show]);

  const handleClose = () => setOpen(false);

  return (
    <>
      {children}
      <Toast
        open={open}
        onClose={handleClose}
        message={message}
        status={status}
      />
    </>
  );
};
